/**
 * Entity-card renderer — paired-side-by-side review card with field
 * diff highlighting + resolution buttons.
 *
 * The AI uses this when it's enriching an entity from multiple sources
 * (e.g. resolving a contact's address from FEC + the org's own
 * directory) and wants the user to pick the right one. Two cards
 * render side by side; fields that differ get highlighted; a
 * confidence ribbon sits between them; resolution buttons trigger a
 * follow-up message back to the AI.
 *
 * Single-card mode (cards.length === 1) is also supported — used for
 * "review this entity" without a comparison.
 */

import { registerRenderer, type RendererProps } from "./registry.js";

export interface EntityField {
  label?: string;
  value?: unknown;
  source_badge?: string;
}

export interface EntityCard {
  label?: string;
  fields?: EntityField[];
}

export interface ResolutionOption {
  action_key?: string;
  label?: string;
}

export interface EntityCardInput {
  title?: string;
  cards?: EntityCard[];
  /** Confidence of the proposed match, 0-1. Renders a vertical ribbon
   *  between the paired cards when present. */
  confidence?: number;
  /** Optional position label, e.g. "3 of 12". */
  queue_position?: string;
  resolution_options?: ResolutionOption[];
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return "(none)";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function valueOf(card: EntityCard | undefined, label: string): unknown {
  if (!card) return undefined;
  const f = (card.fields || []).find(
    (x) => (x.label || "").toLowerCase() === (label || "").toLowerCase()
  );
  return f ? f.value : undefined;
}

function CardInner({
  card,
  otherCard,
}: {
  card: EntityCard;
  otherCard: EntityCard | null;
}) {
  return (
    <div className="flex-1 min-w-0 flex flex-col gap-1.5">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
        {card.label || ""}
      </div>
      {(card.fields || []).map((f, i) => {
        const other = otherCard ? valueOf(otherCard, f.label || "") : undefined;
        const isDiff =
          otherCard && JSON.stringify(other) !== JSON.stringify(f.value);
        return (
          <div
            key={i}
            className={`text-xs flex flex-col gap-0.5 px-2 py-1 rounded ${isDiff ? "bg-amber-50 ring-1 ring-amber-200" : ""}`}
          >
            <div className="text-[10px] uppercase font-semibold text-stone-500 tracking-wider">
              {f.label || ""}
            </div>
            <div className="text-stone-900 break-words">
              {fmt(f.value)}
              {f.source_badge && (
                <span className="ml-1.5 inline-block text-[9px] uppercase tracking-wide font-semibold bg-stone-100 text-stone-600 px-1 py-px rounded">
                  {f.source_badge}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EntityCardCard({
  input,
  consumed,
  onConsume,
}: RendererProps<EntityCardInput>) {
  const cards = Array.isArray(input.cards) ? input.cards : [];
  const isPair = cards.length === 2;
  const confidencePct =
    input.confidence != null ? Math.round(input.confidence * 100) : null;

  const handleResolve = (option: ResolutionOption) => {
    if (consumed) return;
    onConsume?.({
      kind: "entity_resolution",
      payload: { action_key: option.action_key, title: input.title },
      followupMessage: `entity_card resolution: action_key=${option.action_key} for "${input.title || ""}". Continue.`,
    });
  };

  return (
    <div
      className={`self-stretch border border-stone-200 rounded-xl shadow-card bg-white p-3 flex flex-col gap-3 ${consumed ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-base">⫶</span>
        <span className="text-sm font-bold text-stone-900 flex-1">
          {input.title || ""}
        </span>
        {input.queue_position && (
          <span className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded">
            {input.queue_position}
          </span>
        )}
      </div>
      <div className="flex items-stretch gap-2">
        {isPair ? (
          <>
            <CardInner card={cards[0]} otherCard={cards[1]} />
            {confidencePct !== null && (
              <div className="flex flex-col items-center gap-1 px-1">
                <div className="relative w-1 h-24 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className="absolute left-0 right-0 w-1.5 h-1.5 -ml-px rounded-full bg-action"
                    style={{ bottom: `${confidencePct}%` }}
                  />
                </div>
                <div className="text-[10px] font-mono font-semibold text-stone-700 tabular-nums">
                  {confidencePct}%
                </div>
              </div>
            )}
            <CardInner card={cards[1]} otherCard={cards[0]} />
          </>
        ) : cards.length === 1 ? (
          <CardInner card={cards[0]} otherCard={null} />
        ) : null}
      </div>
      {input.resolution_options && input.resolution_options.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {input.resolution_options.map((opt, i) => (
            <button
              key={opt.action_key || i}
              onClick={() => handleResolve(opt)}
              disabled={consumed}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${i === 0 ? "bg-action hover:bg-action-hover text-white" : "bg-white border border-stone-300 text-stone-800 hover:bg-stone-100"}`}
            >
              {opt.label || ""}
            </button>
          ))}
        </div>
      )}
      {consumed && (
        <div className="text-[11px] text-stone-400 italic">resolved</div>
      )}
    </div>
  );
}

registerRenderer<EntityCardInput>({
  tool: "open_entity_card",
  label: "Paired entity-review card with diff + resolution",
  Component: EntityCardCard,
});

export { EntityCardCard };
